import React, { useRef, useState, useEffect } from 'react'
import { Services } from '@/services/services'
import { useForm } from 'react-hook-form'
import { LoggedInPageLayout } from '@/components/layout/LoggedInLayout'
import { addServerErrors, ErrorBoxComponent } from '@/components/forms/common/FormBase'
import { PageQuery, PageResponse, SearchTable, SearchTableParams, SearchTableRef } from '@/components/search/SearchTable'
import { BackButton } from '@/components/layout/NavBar'
import { useNavigate } from 'react-router-dom'
import { useModal } from '@/components/modal/Modal'
import { CurrentSession } from '@/services/repository/session'
import { SimpleUser } from '../users/Users'
import { formatDate } from '@/common/time'

export interface SimpleUserGroup {
  id: string
  name: string
}

export interface UserGroupFormData {
  id?: string
  name: string
}

export interface SuperAdminEditUserGroupPopupProps {
  services: Services
  session: CurrentSession
  onClose: () => void
  userGroupId: string | undefined
}
export interface SuperAdminInsertUserGroupPopupProps {
  services: Services
  session: CurrentSession
  onClose: () => void
  onInsert?: (data: {
    id: string
    name: string
  }) => void
  defaultValues?: UserGroupFormData
}

// -------- POPUP FOR EDIT -----------
export const SuperAdminEditUserGroupPopup = ({ services, session, onClose, userGroupId }: SuperAdminEditUserGroupPopupProps) => {
  const form = useForm<UserGroupFormData>()
  const [data, setData] = useState<UserGroupFormData>()

  useEffect(() => {
    if (!userGroupId) return
    setData(undefined)
    services.session.fetchWithToken(`/api/communal/groups/byid/${userGroupId}`).then(r => r.json()).then(data => {
      setData(data)
      form.reset(data)
    })
  }, [userGroupId])

  const handle = (data: UserGroupFormData): Promise<void> => {
    if (!form) return Promise.reject()
    return services.session.fetchWithToken(`/api/communal/groups/byid/${data.id}/update`, {
      method: 'PUT',
      body: JSON.stringify(data)
    }).then(async (r) => {
      if (r.status !== 200) {
        const errs = await r.json()
        addServerErrors(form, session.translates, errs)
        return
      }
      onClose()
    })
  }

  return data !== undefined ? (
    <form
      onSubmit={form.handleSubmit(handle)}
      className="main-form"
      autoComplete="off"
    >
      <div className='form-field'>
        <label>{session.translates.for('elements.table.groups.name')}</label>
        <input type='text' {...form.register('name', { required: true })} />
        {form.formState.errors.name && <span>{session.translates.for('errors.required')}</span>}
      </div>
      <button className="submit-button" type="submit" disabled={!form.formState.errors}>
        {session.translates.for(`forms.groups.submit`)}
      </button>
    </form>
  ) : <div className="spinner"></div>
}

// -------- POPUP FOR INSERT -----------
export const SuperAdminInsertUserGroupPopup = ({ services, session, onClose, onInsert, defaultValues }: SuperAdminInsertUserGroupPopupProps) => {
  const form = useForm<UserGroupFormData>({ defaultValues })

  const handle = (data: UserGroupFormData): Promise<void> => {
    if (!form) return Promise.reject()
    delete data.id
    return services.session.fetchWithToken(`/api/communal/groups`, {
      method: 'POST',
      body: JSON.stringify(data),
    }).then(async (r) => {
      if (r.status !== 200) {
        const errs = await r.json()
        addServerErrors(form, session.translates, errs)
        return
      }
      form.reset()
      onClose()
      if (onInsert) {
        const respData = await r.json()
        onInsert({
          ...respData,
          name: data.name
        })
      }
    })
  }

  return (
    <form
      onSubmit={form.handleSubmit(handle)}
      className="main-form"
      autoComplete="off"
    >
      <div className='form-field'>
        <label>{session.translates.for('elements.table.groups.name')}</label>
        <input type='text' {...form.register('name', { required: true })} />
        {form.formState.errors.name && <p className="errorMessage">{session.translates.for('errors.required')}</p>}
      </div>
      <button className="submit-button" type="submit" disabled={!form.formState.errors}>
        {session.translates.for(`forms.groups.submit`)}
      </button>
    </form>
  )
}

// -------- USER RELATION POPUP --------

const RelatedUsersPage = ({
  services,
  session,
  groupId,
  onClose
}: {
  services: Services,
  session: CurrentSession,
  groupId: string | undefined,
  onClose: () => void
}) => {
  const searchTableRef = useRef<SearchTableRef>(null)

  const tableParams: SearchTableParams<SimpleUser> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleUser>> => {
      return services.session.fetchWithToken(`/api/communal/groups/byid/${groupId}/users/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <></>,
    header: () => <>
      <div className='hcol'></div>
      <div className='hcol desktop-only'>{session.translates.for('elements.table.users.createdAt')}</div>
      <div className='hcol desktop-only'>{session.translates.for('elements.table.users.email')}</div>
      <div className='hcol'>{session.translates.for('elements.table.users.username')}</div>
    </>,
    row: (row, state, setState) => <>
      <div className='col icocol' onClick={() => {
        setState((state) => {
          if (state === undefined) return;
          state.data = state.data?.map(item =>
            item.id === row.id ? { ...item, isRelated: undefined } : item
          )
          return { ...state }
        })

        services.session.fetchWithToken(`/api/communal/groups/byid/${groupId}/relations/users/${row.id}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id,
            related: !row.isRelated
          }),
        }).then((resp) => {
          setState((state) => {
            if (state === undefined) return;
            state.data = state.data?.map(item =>
              item.id === row.id ? { ...item, isRelated: resp.ok ? !row.isRelated : row.isRelated } : item
            )
            return { ...state }
          })
        })

      }}>
        {row.isRelated === undefined ? <div className='spinner'></div> : row.isRelated ? <i className='svgfont-checkbox' /> : <i className='svgfont-checkbox-blank' />}
      </div>
      <div className='col desktop-only'>{formatDate(new Date(row.createdAt))}</div>
      <div className='col desktop-only'>{row.email}</div>
      <div className='col'>{row.username}</div>
    </>,
    translates: session.translates
  }

  return <SearchTable ref={searchTableRef} {...tableParams} />
}

// export default RelatedUsersPage;

// ------- MACHINE RELATION POPUP ------

export interface SimpleMachine {
  id: string
  name: string
  manufacturer: string
  model: string
  internalNumber: string
  isRelated?: boolean
}

const RelatedMachinesPage = ({
  services,
  session,
  groupId,
  onClose
}: {
  services: Services,
  session: CurrentSession,
  groupId: string | undefined,
  onClose: () => void
}) => {
  const searchTableRef = useRef<SearchTableRef>(null)

  const tableParams: SearchTableParams<SimpleMachine> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleMachine>> => {
      return services.session.fetchWithToken(
        `/api/communal/groups/byid/${groupId}/machines/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`
      ).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => <></>,
    header: () => <>
      <div className='hcol'></div>
      <div className='hcol'>{session.translates.for('elements.table.machines.manufacturer')}</div>
      <div className='hcol'>{session.translates.for('elements.table.machines.model')}</div>
      <div className='hcol'>{session.translates.for('elements.table.machines.number')}</div>
      {/* <div className='hcol'>{session.translates.for('elements.table.machines.name')}</div> */}
    </>,
    row: (row, state, setState) => <>
      <div className='col icocol' onClick={() => {
        setState((state) => {
          if (state === undefined) return
          state.data = state.data?.map(item =>
            item.id === row.id ? { ...item, isRelated: undefined } : item
          )
          return { ...state }
        })

        services.session.fetchWithToken(`/api/communal/groups/byid/${groupId}/relations/machines/${row.id}`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id: row.id,
            related: !row.isRelated
          }),
        }).then((resp) => {
          setState((state) => {
            if (state === undefined) return
            state.data = state.data?.map(item =>
              item.id === row.id ? { ...item, isRelated: resp.ok ? !row.isRelated : row.isRelated } : item
            )
            return { ...state }
          })
        })

      }}>
        {row.isRelated === undefined ? <div className='spinner'></div> : row.isRelated ? <i className='svgfont-checkbox' /> : <i className='svgfont-checkbox-blank' />}
      </div>
      <div className='col'>{row.manufacturer}</div>
      <div className='col'>{row.model}</div>
      <div className='col'>{row.internalNumber || row.id}</div>
      {/* <div className='col'>{row.name}</div> */}
    </>,
    translates: session.translates
  }

  return <SearchTable ref={searchTableRef} {...tableParams} />
}

// ------- REMOVE USER POPUP -----------

type SuperAdminRemoveUserGroupPopupProps = {
  services: Services,
  session: CurrentSession,
  onClose: () => void,
  group: { id: string, name: string } | undefined
}

export const SuperAdminRemoveUserGroupPopup = ({ services, session, onClose, group }: SuperAdminRemoveUserGroupPopupProps) => {
  const form = useForm()
  const [error, setError] = useState<boolean>(false)

  useEffect(() => {
    setError(false)
  }, [group?.id])

  if (group === undefined) {
    return null
  }

  const remove = (): Promise<void> => {
    return services.session.fetchWithToken(`/api/communal/groups/byid/${group.id}/delete`, {
      method: 'DELETE'
    }).then(async (r) => {
      if (r.status !== 200) {
        setError(true)
        return
      }
      setError(false)
      onClose()
    })
  }

  return (
    <div className='dialog'>
      <div className='dialog-text'>
        {session.translates.for('elements.table.usergroups.remove_group_ask').replace('{groupname}', group.name)}
      </div>
      <div className='dialog-btns'>
        <div className='dialog-btn' onClick={onClose}>
          <i className='svgfont-no' />
          {session.translates.for('elements.dialog.yesno.no')}
        </div>
        <div className='dialog-btn' onClick={remove}>
          <i className='svgfont-yes' />
          {session.translates.for('elements.dialog.yesno.yes')}
        </div>
        {error ? (
          <div className='dialog-error'>
            {session.translates.for('elements.table.usergroups.remove_group_error')}
          </div>
        ) : null}
      </div>
    </div>
  )
}

// -------- USER GROUPS PAGE -----------
export const SuperAdminUserGroupsPage = ({ services, session }: { services: Services, session: CurrentSession }) => {
  const searchTableRef = useRef<SearchTableRef>(null)
  const { openModal } = useModal()
  const navigate = useNavigate()

  const tableParams: SearchTableParams<SimpleUserGroup> = {
    fetch: (q: PageQuery): Promise<PageResponse<SimpleUserGroup>> => {
      return services.session.fetchWithToken(`/api/communal/groups/search?pageNumber=${q.pageNumber || ''}&pageSize=${q.pageSize || ''}&search=${q.search || ''}`).then((r) => {
        if (r.status === 200) {
          return r.json()
        }
        return Promise.reject()
      })
    },
    pageQuery: {
      pageNumber: 1,
      pageSize: 30,
      search: ""
    },
    toolbox: () => (
      <div className='tool' onClick={() => openModal((props) =>
        SuperAdminInsertUserGroupPopup({ ...props, services, session })
      )}>
        <i className='svgfont-add' />
      </div>
    ),
    header: () => (
      <>
        <div className='hcol'>{session.translates.for('elements.table.groups.name')}</div>
        <div className='hcol'></div>
        <div className='hcol'></div>
        <div className='hcol'></div>
        <div className='hcol'></div>
      </>
    ),
    row: (row) => (
      <>
        <div className='col'>{row.name}</div>
        <div className='col icocol' onClick={() => openModal((props) =>
          SuperAdminEditUserGroupPopup({ ...props, services, session, userGroupId: row.id })
        )}>
          <i className='svgfont-edit' />
        </div>
        <div className='col icocol' onClick={() => openModal((props) => RelatedUsersPage({ ...props, groupId: row.id, services, session }))}>
          <i className='svgfont-users' />
        </div>
        <div className='col icocol' onClick={() => openModal((props) => RelatedMachinesPage({ ...props, groupId: row.id, services, session }))}>
          <i className='svgfont-machine' />
        </div>
        <div className='col icocol' onClick={() => openModal((props) => SuperAdminRemoveUserGroupPopup({ ...props, group: row, services, session }))}>
          <i className='svgfont-remove' />
        </div>
      </>
    ),
    translates: session.translates
  }

  return (
    <LoggedInPageLayout pageName='superadmin-usergroups' services={services} session={session}
      navbarLeft={<BackButton services={services} translates={session.translates} />}>
      <SearchTable ref={searchTableRef} {...tableParams} />
    </LoggedInPageLayout>
  )
}